import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@trillion/ui/components/ui/button";
import {
  Card,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@trillion/ui/components/ui/card";
import { passportApi } from "../lib/passport-api";

/**
 * Account-page "Verify your email" panel — rendered only while the signed-in user is unverified.
 * Verification is soft (it gates nothing), so this is a nudge, not a wall. Each send mints a fresh
 * token and retires the previous one, and the endpoint is rate-limited per account (3 per 5 minutes),
 * so a fourth press inside the window surfaces the server's 429 as an error toast.
 */
export function ResendVerificationSection({ email }: { email: string }) {
  const [submitting, setSubmitting] = useState(false);
  // Flips after the first successful send → swap the initial button for the "we sent it" notice.
  const [sent, setSent] = useState(false);

  async function handleSend() {
    setSubmitting(true);
    try {
      await passportApi.auth.resendVerification();
      setSent(true);
      toast.success("Verification email sent.");
    } catch (err) {
      // 409 already_verified / 429 rate limit / 502 email_send_failed — server body in the message.
      toast.error(err instanceof Error ? err.message : "Failed to send the verification email");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Card className="mt-4">
      <CardHeader className="space-y-1">
        <CardTitle className="text-lg font-semibold">Verify your email</CardTitle>
        <CardDescription>
          {sent ? (
            <>
              We sent a verification link to <span className="font-medium">{email}</span>. Open it to
              finish verifying — the most recent link is the only one that works.
            </>
          ) : (
            <>
              Your address <span className="font-medium">{email}</span> isn't verified yet. We'll email
              you a link that confirms it.
            </>
          )}
        </CardDescription>
      </CardHeader>
      <CardFooter>
        <Button
          type="button"
          variant={sent ? "outline" : "default"}
          className="w-full"
          onClick={handleSend}
          disabled={submitting}
        >
          {submitting ? "Sending…" : sent ? "Send again" : "Send verification email"}
        </Button>
      </CardFooter>
    </Card>
  );
}
